import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { SysUser } from "../types/user";
import { fetchAvatar } from "../services/userService"

interface UserContextType {
    user: SysUser | null;
    login: (userData: SysUser) => void;
    updateUser: (userData: SysUser) => void;
    logout: () => void;
    handleFetchAvatar: (userId: number) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<SysUser | null>(null);

    useEffect(() => {
        // Check localStorage for user data on initial load
        const userData = localStorage.getItem("user");
        if (userData) {
            setUser(JSON.parse(userData)); // Set the user from localStorage
        }
    }, []);

    const updateUser = (partialData: Partial<SysUser>) => {
        setUser((prevUser) => {
            if (!prevUser) return prevUser;
            const updatedUser: SysUser = { ...prevUser, ...partialData } as SysUser;
            localStorage.setItem("user", JSON.stringify(updatedUser));
            return updatedUser;
        });
    };

    const login = (userData: SysUser) => {
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem("user");
        window.location.href = "/"
    };

    const handleFetchAvatar = async (userId: number) => {
        const response = await fetchAvatar(userId);
        setUser((prev) => prev ? { ...prev, avatar: response.avatar } : prev);
    }

    return (
        <UserContext.Provider value={{ user, login, logout, handleFetchAvatar, updateUser }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return context;
};
